import React from "react";
import type SwiperInstance from "swiper";
import { Swiper, SwiperSlide } from "../Swiper";

import type { StorySummary } from "../../@types/portalAluno";
import FastImage from "../FastImage/FastImage";
import { preloadImageSources } from "../../services/portalAluno/images/imageCache";
import "swiper/swiper.min.css";
import "./styles.css";

const AVATAR_SIZE = 104;

const EAGER_AVATARS = 4;

interface StoryCarouselProps {
  stories: StorySummary[];
  onOpenStory: (storyIndex: number) => void;
  hasMoreStories?: boolean;
  isLoadingMoreStories?: boolean;
  onLoadMoreStories?: () => void;
}

interface StoryCarouselItemProps {
  story: StorySummary;
  storyIndex: number;
  eager: boolean;
  onOpenStory: (storyIndex: number) => void;
}

const getInitials = (title: string) =>
  title
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toLocaleUpperCase("pt-BR"))
    .join("");

const StoryCarouselItem = React.memo(
  ({ story, storyIndex, eager, onOpenStory }: StoryCarouselItemProps) => {
    const handleClick = React.useCallback(
      () => onOpenStory(storyIndex),
      [onOpenStory, storyIndex]
    );

    const initials = React.useMemo(
      () => getInitials(story.title),
      [story.title]
    );

    return (
      <button
        type="button"
        className={`story-carousel__item${
          story.seen ? " story-carousel__item--seen" : ""
        }`}
        onClick={handleClick}
        aria-label={`Abrir o story ${story.title}`}
      >
        <span className="story-carousel__ring">
          <span className="story-carousel__avatar">
            <FastImage
              src={story.imageUrl}
              alt=""
              width={AVATAR_SIZE}
              height={AVATAR_SIZE}
              draggable={false}
              priority={eager}
              skeleton={false}
              fallback={
                <span className="story-carousel__initials" aria-hidden="true">
                  {initials}
                </span>
              }
            />
          </span>
        </span>
        <span className="story-carousel__name">{story.title}</span>
      </button>
    );
  }
);

StoryCarouselItem.displayName = "StoryCarouselItem";

const StoryCarousel = ({
  stories,
  onOpenStory,
  hasMoreStories = false,
  isLoadingMoreStories = false,
  onLoadMoreStories,
}: StoryCarouselProps) => {
  const swiperRef = React.useRef<SwiperInstance>();
  const requestedLengthRef = React.useRef<number>();
  const canLoadMore = Boolean(
    hasMoreStories && onLoadMoreStories && !isLoadingMoreStories
  );

  const requestNextPage = React.useCallback(() => {
    if (!canLoadMore || requestedLengthRef.current === stories.length) return;

    requestedLengthRef.current = stories.length;
    onLoadMoreStories?.();
  }, [canLoadMore, onLoadMoreStories, stories.length]);

  React.useEffect(() => {
    const swiper = swiperRef.current;
    if (swiper && swiper.isEnd) requestNextPage();
  }, [requestNextPage, stories.length]);

  React.useEffect(() => {
    void preloadImageSources(stories.map((story) => story.imageUrl));
  }, [stories]);

  if (stories.length === 0) return null;

  return (
    <section className="story-carousel" aria-label="Stories">
      <Swiper
        className="story-carousel__track"
        cssMode
        slidesPerView="auto"
        spaceBetween={20}
        breakpoints={{
          0: { spaceBetween: 14 },
          790: { spaceBetween: 20 },
        }}
        freeMode
        freeModeMomentum
        freeModeMomentumBounce={false}
        freeModeMomentumRatio={0.75}
        grabCursor
        speed={360}
        watchOverflow
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          if (swiper.isEnd) requestNextPage();
        }}
        onReachEnd={requestNextPage}
        a11y={{
          containerMessage: "Carrossel de stories",
          slideLabelMessage: "Story {{index}} de {{slidesLength}}",
        }}
      >
        {stories.map((story, storyIndex) => (
          <SwiperSlide key={story.id}>
            <StoryCarouselItem
              story={story}
              storyIndex={storyIndex}
              eager={storyIndex < EAGER_AVATARS}
              onOpenStory={onOpenStory}
            />
          </SwiperSlide>
        ))}

        {hasMoreStories && (
          <SwiperSlide>
            <span
              className="story-carousel__loader"
              role="status"
              aria-label="Carregando mais stories"
            >
              <span className="story-carousel__spinner" aria-hidden="true" />
            </span>
          </SwiperSlide>
        )}
      </Swiper>
    </section>
  );
};

export default React.memo(StoryCarousel);
